/**
 * Motion helpers shared by the list / reader / calendar: how long a removed row takes to leave, whether the user asked for
 * reduced motion, and the pure bookkeeping for rows on their way out.
 */

/** A removed row slides out, then its height closes (app.css .row.is-leaving). */
export const LEAVE_MS = 380;

export function reducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Archive / delete / move of `ids`: which of them may start leaving (rows already leaving are skipped, so a second click or key
 * press can't act twice), and what to select next. `next` is undefined when the selection isn't among the removed rows (keep
 * it), null when nothing is left to select.
 */
export function planLeave<T extends { id: number }>(
  list: readonly T[],
  ids: readonly number[],
  leaving: ReadonlySet<number>,
  selectedId: number | null,
): { ids: number[]; next: T | null | undefined } {
  // Ids that aren't list rows (a single message inside a conversation) still go through, just without a row to animate
  const fresh = ids.filter((id) => !leaving.has(id));
  if (!fresh.length) return { ids: [], next: undefined };
  if (selectedId == null || !fresh.includes(selectedId)) return { ids: fresh, next: undefined };
  const idx = list.findIndex((m) => m.id === selectedId);
  // The rows that stay: neither being removed now nor already on their way out
  const gone = new Set([...fresh, ...leaving]);
  const after = list.slice(idx + 1).find((m) => !gone.has(m.id));
  const before = [...list.slice(0, idx)].reverse().find((m) => !gone.has(m.id));
  return { ids: fresh, next: after ?? before ?? null };
}

/** Rows that appeared above the previously first known row since the last render (new mail at the top), for a gentle entrance. */
export function newAtTop(prev: readonly number[], current: readonly number[]): number[] {
  if (!prev.length) return [];
  const known = new Set(prev);
  const out: number[] = [];
  for (const id of current) {
    if (known.has(id)) break;
    out.push(id);
  }
  // A completely different list (another folder / search) isn't "new mail"
  return out.length === current.length ? [] : out;
}
